/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  accessAPI,
  downloadFile,
  formatFecha,
  Boton,
  Titulo,
  CampoInformacion,
  Formulario, tituloBase } from "../../../../shared";

import { Toast } from "primereact/toast";
import { Card } from "primereact/card";
import { Skeleton } from "primereact/skeleton";
import { Sidebar, VisorArchivo, TablaFiltrable } from "../../../../shared";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { Divider } from "primereact/divider";
import { Menu } from "primereact/menu";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";

import TarjetaAsignarNorma from "./TarjetaAsignarNorma";
import TarjetaAsignarEsquema from "./TarjetaAsignarEsquema";

// Etiquetas que el backend reemplaza automáticamente al subir una versión.
// Ver morfeo-api/utils/sustitucionDocumento.js — el objeto de datos es {version}
// más las fechas calculadas en el momento de la sustitución.
const ETIQUETAS_DOCUMENTO = [
  { tag: "[[version]]", descripcion: "número de versión" },
  { tag: "[[fechaDMA]]", descripcion: "fecha actual (DD/MM/AAAA)" },
  { tag: "[[fechaD]]", descripcion: "día (DD)" },
  { tag: "[[fechaM]]", descripcion: "mes (MM)" },
  { tag: "[[fechaA]]", descripcion: "año (AAAA)" },
  { tag: "[[fechaMNombre]]", descripcion: "nombre del mes" }
];

function EtiquetasDisponibles() {
  return (
    <div
      css={{
        border: "1px solid var(--surface-border)",
        borderRadius: "6px",
        padding: "0.75em 1em",
        marginBottom: "1em",
        fontSize: "0.85em"
      }}
    >
      <div
        css={{
          display: "flex",
          alignItems: "center",
          gap: "0.5em",
          marginBottom: "0.5em",
          fontWeight: 600
        }}
      >
        <i className="pi pi-info-circle" />
        <span>Etiquetas disponibles</span>
      </div>
      <p css={{ margin: "0 0 0.5em", color: "var(--text-color-secondary)" }}>
        En archivos <strong>.docx</strong> y <strong>.xlsx</strong> podés
        insertar estas etiquetas y se reemplazan automáticamente al subir la
        versión:
      </p>
      <ul
        css={{
          margin: 0,
          paddingLeft: "1.2em",
          display: "flex",
          flexDirection: "column",
          gap: "0.2em"
        }}
      >
        {ETIQUETAS_DOCUMENTO.map((e) => (
          <li key={e.tag}>
            <code>{e.tag}</code> — {e.descripcion}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function Documento() {
  const [documento, setDocumento] = useState(null);
  const [sideBar, setSideBar] = useState(false);
  const [formLoader, setFormLoader] = useState(false);
  const [sideBarEditar, setSideBarEditar] = useState(false);
  const [loaderEditar, setLoaderEditar] = useState(false);
  const [descargandoId, setDescargandoId] = useState(null);
  const [descargandoVigente, setDescargandoVigente] = useState(false);
  const [visorVigente, setVisorVigente] = useState(false);
  const [versionSeleccionada, setVersionSeleccionada] = useState(null);
  const [sideBarEditarVersion, setSideBarEditarVersion] = useState(false);
  const [loaderEditarVersion, setLoaderEditarVersion] = useState(false);
  const [sideBarDetalleVersion, setSideBarDetalleVersion] = useState(false);
  const [eliminandoDocumento, setEliminandoDocumento] = useState(false);
  const [sideBarInstrucciones, setSideBarInstrucciones] = useState(false);
  const [editandoInstrucciones, setEditandoInstrucciones] = useState(false);
  const [loaderInstrucciones, setLoaderInstrucciones] = useState(false);
  const { documentoId } = useParams();
  const navigate = useNavigate();
  const toast = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    cargarDocumento();
    return () => (document.title = tituloBase());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Polling de los procesos con IA en curso (comparación de versiones y
  // extracción de texto para los asistentes). Corren en background en el
  // backend (pueden demorar más que el timeout HTTP), así que cuando hay al
  // menos una versión procesándose hacemos polling hasta que termine y
  // refrescamos solo esa versión en el state.
  useEffect(() => {
    if (!documento) return;
    const procesando = documento.documentoVersions.filter(
      (v) =>
        v.estadoComparacion?.status === "procesando" ||
        v.estadoExtraccion === "PROCESANDO"
    );
    if (procesando.length === 0) return;

    const intervalId = setInterval(() => {
      procesando.forEach((version) => {
        accessAPI(
          "GET",
          `admin/documento/${documentoId}/version/${version.id}/estadoComparacion`,
          null,
          (respuesta) => {
            if (
              respuesta.estadoComparacion?.status === "procesando" ||
              respuesta.estadoExtraccion === "PROCESANDO"
            )
              return;
            setDocumento((prev) => {
              if (!prev) return prev;
              return {
                ...prev,
                documentoVersions: prev.documentoVersions.map((v) =>
                  v.id === version.id
                    ? {
                        ...v,
                        estadoComparacion: respuesta.estadoComparacion,
                        controlCambios: respuesta.controlCambios,
                        notasCambios: respuesta.notasCambios,
                        estadoExtraccion: respuesta.estadoExtraccion,
                        resumenAsistente: respuesta.resumenAsistente,
                        detalleErrorExtraccion: respuesta.detalleErrorExtraccion
                      }
                    : v
                )
              };
            });
          },
          () => {}
        );
      });
    }, 5000);

    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documento, documentoId]);

  // Mantener versionSeleccionada sincronizada cuando el polling actualiza la versión
  // que el usuario tiene abierta en el sidebar de detalles.
  useEffect(() => {
    if (!versionSeleccionada || !documento) return;
    const actualizada = documento.documentoVersions.find(
      (v) => v.id === versionSeleccionada.id
    );
    if (actualizada && actualizada !== versionSeleccionada) {
      setVersionSeleccionada(actualizada);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documento]);

  function cargarDocumento() {
    accessAPI(
      "GET",
      `admin/documento/${documentoId}`,
      null,
      (respuesta) => {
        setDocumento(respuesta);
        document.title = respuesta.nombre;
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al cargar el documento",
          sticky: true
        });
      }
    );
  }

  function crearVersion(camposFormulario, archivo) {
    setFormLoader(true);
    accessAPI(
      "POST",
      `admin/documento/${documentoId}/version`,
      camposFormulario,
      () => {
        setFormLoader(false);
        setSideBar(false);
        cargarDocumento();
        toast.current?.show({
          severity: "success",
          summary: "Creada",
          detail: "Versión creada exitosamente"
        });
      },
      (respuesta) => {
        setFormLoader(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al crear la versión",
          sticky: true
        });
      },
      archivo
    );
  }

  // Nombre de descarga: {codigo} - {nombre} - v.{version}, conservando la
  // extensión del archivo original
  function nombreDescargaVersion(version, nombreOriginal) {
    const ext = nombreOriginal?.includes(".")
      ? `.${nombreOriginal.split(".").pop()}`
      : "";
    return `${documento.codigo} - ${documento.nombre} - v.${version.version}${ext}`;
  }

  function descargarArchivo(version) {
    setDescargandoId(version.id);
    downloadFile(
      `admin/archivo/${version.archivoId}`,
      nombreDescargaVersion(version, version.archivo.nombredescarga),
      () => setDescargandoId(null),
      (error) => {
        setDescargandoId(null);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: error[0]?.msg || "Error al descargar archivo",
          sticky: true
        });
      }
    );
  }

  function descargarArchivoPDF(version) {
    if (!version.archivoPDFId) return;
    setDescargandoId(version.id);
    downloadFile(
      `admin/archivo/${version.archivoPDFId}`,
      nombreDescargaVersion(version, version.archivoPDF?.nombredescarga),
      () => setDescargandoId(null),
      (error) => {
        setDescargandoId(null);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: error[0]?.msg || "Error al descargar PDF",
          sticky: true
        });
      }
    );
  }

  function descargarVersionVigente() {
    const liberadas = documento.documentoVersions.filter((v) => v.liberado);
    if (liberadas.length === 0) return;
    const ultima = liberadas[0]; // ya vienen ordenadas por id desc
    // Si la versión vigente tiene PDF generado, priorizarlo sobre el editable
    const archivoIdDescarga = ultima.archivoPDFId || ultima.archivoId;
    const nombreDescarga = nombreDescargaVersion(
      ultima,
      ultima.archivoPDFId
        ? ultima.archivoPDF?.nombredescarga
        : ultima.archivo.nombredescarga
    );
    setDescargandoVigente(true);
    downloadFile(
      `admin/archivo/${archivoIdDescarga}`,
      nombreDescarga,
      () => setDescargandoVigente(false),
      (error) => {
        setDescargandoVigente(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: error[0]?.msg || "Error al descargar archivo",
          sticky: true
        });
      }
    );
  }

  function editarVersionSubmit(camposFormulario, archivo) {
    setLoaderEditarVersion(true);
    accessAPI(
      "PUT",
      `admin/documento/${documentoId}/version/${versionSeleccionada.id}`,
      camposFormulario,
      (respuesta) => {
        setDocumento(respuesta);
        setSideBarEditarVersion(false);
        setLoaderEditarVersion(false);
        toast.current?.show({
          severity: "success",
          summary: "Actualizada",
          detail: "Versión actualizada exitosamente"
        });
      },
      (respuesta) => {
        setLoaderEditarVersion(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al actualizar la versión",
          sticky: true
        });
      },
      archivo
    );
  }

  function eliminarVersion(version) {
    confirmDialog({
      message: `¿Está seguro que desea eliminar la versión ${version.version}?`,
      header: "Confirmar eliminación",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      acceptLabel: "Eliminar",
      rejectLabel: "Cancelar",
      accept: () => {
        accessAPI(
          "DELETE",
          `admin/documento/${documentoId}/version/${version.id}`,
          null,
          (respuesta) => {
            setDocumento(respuesta);
            toast.current?.show({
              severity: "success",
              summary: "Eliminada",
              detail: `Versión ${version.version} eliminada exitosamente`
            });
          },
          (respuesta) => {
            toast.current?.show({
              severity: "error",
              summary: "Error",
              detail: respuesta[0]?.msg || "Error al eliminar la versión",
              sticky: true
            });
          }
        );
      }
    });
  }

  function liberarVersion(version) {
    accessAPI(
      "PUT",
      `admin/documento/${documentoId}/version/${version.id}/liberar`,
      null,
      (respuesta) => {
        setDocumento(respuesta);
        toast.current?.show({
          severity: "success",
          summary: "Liberada",
          detail: `Versión ${version.version} liberada exitosamente`
        });
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al liberar la versión",
          sticky: true
        });
      }
    );
  }

  function eliminarDocumento() {
    confirmDialog({
      message:
        "¿Está seguro que desea eliminar este documento? Se eliminarán todas sus versiones y archivos asociados de forma permanente.",
      header: "Confirmar eliminación",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Eliminar",
      rejectLabel: "Cancelar",
      acceptClassName: "p-button-danger",
      accept: () => {
        setEliminandoDocumento(true);
        accessAPI(
          "DELETE",
          `admin/documento/${documentoId}`,
          null,
          () => {
            navigate("/documento");
          },
          (respuesta) => {
            setEliminandoDocumento(false);
            toast.current?.show({
              severity: "error",
              summary: "Error",
              detail: respuesta[0]?.msg || "Error al eliminar el documento",
              sticky: true
            });
          }
        );
      }
    });
  }

  // Vuelve a disparar la comparación con IA de una versión contra la versión
  // liberada anterior. Al marcar la versión como "procesando" en el state, el
  // useEffect de polling retoma el seguimiento hasta que termine.
  function reintentarComparacion(version) {
    accessAPI(
      "POST",
      `admin/documento/${documentoId}/version/${version.id}/comparar`,
      null,
      (respuesta) => {
        setDocumento((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            documentoVersions: prev.documentoVersions.map((v) =>
              v.id === version.id
                ? { ...v, estadoComparacion: respuesta.estadoComparacion }
                : v
            )
          };
        });
        toast.current?.show({
          severity: "success",
          summary: "Comparación relanzada",
          detail: "Generando la comparación con IA, puede demorar unos minutos"
        });
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al relanzar la comparación",
          sticky: true
        });
      }
    );
  }

  const menuItems = [
    ...(versionSeleccionada?.archivoPDFId
      ? [
          {
            label: "Descargar editable",
            icon: "pi pi-download",
            command: () => {
              if (versionSeleccionada) {
                descargarArchivo(versionSeleccionada);
              }
            }
          },
          {
            label: documento?.convertirPDF
              ? "Descargar PDF"
              : "Descargar esta versión",
            icon: documento?.convertirPDF ? "pi pi-file-pdf" : "pi pi-download",
            command: () => {
              if (versionSeleccionada) {
                descargarArchivoPDF(versionSeleccionada);
              }
            }
          }
        ]
      : [
          {
            label: "Descargar",
            icon: "pi pi-download",
            command: () => {
              if (versionSeleccionada) {
                descargarArchivo(versionSeleccionada);
              }
            }
          }
        ]),
    {
      label: "Ver detalles",
      icon: "pi pi-info-circle",
      command: () => {
        if (versionSeleccionada) {
          setSideBarDetalleVersion(true);
        }
      }
    },
    ...(versionSeleccionada?.estadoComparacion?.status === "error"
      ? [
          {
            label: "Reintentar comparación",
            icon: "pi pi-sync",
            command: () => {
              if (versionSeleccionada) {
                reintentarComparacion(versionSeleccionada);
              }
            }
          }
        ]
      : []),
    ...(versionSeleccionada && !versionSeleccionada.liberado
      ? [
          {
            label: "Editar",
            icon: "pi pi-pencil",
            command: () => {
              if (versionSeleccionada) {
                setSideBarEditarVersion(true);
              }
            }
          }
        ]
      : []),
    ...(versionSeleccionada && !versionSeleccionada.liberado
      ? [
          {
            label: "Liberar versión",
            icon: "pi pi-check-circle",
            command: () => {
              if (versionSeleccionada) {
                liberarVersion(versionSeleccionada);
              }
            }
          }
        ]
      : []),
    ...(versionSeleccionada && !versionSeleccionada.liberado
      ? [
          { separator: true },
          {
            label: "Eliminar",
            icon: "pi pi-trash",
            className: "p-menuitem-danger",
            command: () => {
              if (versionSeleccionada) {
                eliminarVersion(versionSeleccionada);
              }
            }
          }
        ]
      : [])
  ];

  function editarDocumentoSubmit(camposFormulario) {
    setLoaderEditar(true);
    accessAPI(
      "PUT",
      `admin/documento/${documentoId}`,
      camposFormulario,
      (respuesta) => {
        setDocumento(respuesta);
        document.title = respuesta.nombre;
        setSideBarEditar(false);
        setLoaderEditar(false);
        toast.current?.show({
          severity: "success",
          summary: "Actualizado",
          detail: "Documento actualizado exitosamente"
        });
      },
      (respuesta) => {
        setLoaderEditar(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al actualizar el documento",
          sticky: true
        });
      }
    );
  }

  const editarFormConfig = documento
    ? {
        secciones: [
          {
            campos: [
              {
                nombre: "codigo",
                tipoitemformularioid: 1,
                etiqueta: "Código",
                requerido: true,
                respuesta: documento.codigo
              },
              {
                nombre: "nombre",
                tipoitemformularioid: 1,
                etiqueta: "Nombre",
                requerido: true,
                respuesta: documento.nombre
              },
              {
                nombre: "resumen",
                tipoitemformularioid: 11,
                etiqueta: "Resumen",
                respuesta: documento.resumen
              },
              {
                nombre: "documentoCarpetaId",
                tipoitemformularioid: 5,
                etiqueta: "Carpeta",
                opciones: "admin/documentocarpeta",
                sinOpcionInicial: true,
                opcionVacia: true,
                respuesta: documento.documentoCarpetaId
              },
              {
                nombre: "listaDistribucion",
                tipoitemformularioid: 11,
                etiqueta: "Lista de distribución (correos separados por coma)",
                respuesta: documento.listaDistribucion || ""
              },
              {
                nombre: "vigente",
                tipoitemformularioid: 6,
                etiqueta: "Vigente",
                respuesta: documento.vigente
              },
              {
                nombre: "disponibleAuditor",
                tipoitemformularioid: 6,
                etiqueta: "Disponible para colaboradores",
                respuesta: documento.disponibleAuditor
              },
              {
                nombre: "disponiblePublico",
                tipoitemformularioid: 6,
                etiqueta: "Disponible público",
                respuesta: documento.disponiblePublico
              },
              {
                nombre: "disponibleAsistente",
                tipoitemformularioid: 6,
                etiqueta: "Disponible para el asistente de clientes",
                respuesta: documento.disponibleAsistente
              },
              ...(documento.externo
                ? [
                    {
                      nombre: "linkExterno",
                      tipoitemformularioid: 1,
                      etiqueta: "Link externo",
                      respuesta: documento.linkExterno || ""
                    }
                  ]
                : [
                    {
                      nombre: "convertirPDF",
                      tipoitemformularioid: 6,
                      etiqueta: "Convertir a PDF al subir nueva versión",
                      respuesta: documento.convertirPDF
                    }
                  ])
            ]
          }
        ],
        botones: [
          {
            etiqueta: "Guardar cambios",
            callback: editarDocumentoSubmit,
            validar: true
          }
        ]
      }
    : null;

  // Las instrucciones para el asistente se editan aparte, en su propio sidebar.
  // El endpoint es el mismo de edición del documento, que exige código y
  // nombre y pisa el resumen: se reenvían los valores actuales.
  function guardarInstrucciones(camposFormulario) {
    setLoaderInstrucciones(true);
    accessAPI(
      "PUT",
      `admin/documento/${documentoId}`,
      {
        codigo: documento.codigo,
        nombre: documento.nombre,
        resumen: documento.resumen,
        instruccionesAlAsistente: camposFormulario.instruccionesAlAsistente
      },
      (respuesta) => {
        setLoaderInstrucciones(false);
        setEditandoInstrucciones(false);
        setDocumento((prev) => ({
          ...prev,
          instruccionesAlAsistente: respuesta.instruccionesAlAsistente
        }));
        toast.current?.show({
          severity: "success",
          summary: "Guardado",
          detail: "Instrucciones actualizadas",
          life: 3000
        });
      },
      (respuesta) => {
        setLoaderInstrucciones(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "No se pudieron guardar las instrucciones",
          sticky: true
        });
      }
    );
  }

  const instruccionesFormConfig = {
    secciones: [
      {
        campos: [
          {
            nombre: "instruccionesAlAsistente",
            tipoitemformularioid: 11,
            etiqueta: "Instrucciones para el asistente de IA",
            respuesta: documento?.instruccionesAlAsistente
          }
        ]
      }
    ],
    botones: [
      {
        etiqueta: "Cancelar",
        callback: () => {
          // Sin instrucciones cargadas no hay vista a la que volver
          if (documento?.instruccionesAlAsistente) {
            setEditandoInstrucciones(false);
          } else {
            setSideBarInstrucciones(false);
          }
        },
        validar: false,
        transparente: true
      },
      {
        etiqueta: "Guardar",
        callback: guardarInstrucciones,
        validar: false,
        clases: "verde"
      }
    ]
  };

  const nuevaVersionFormConfig = {
    secciones: [
      {
        campos: [
          {
            nombre: "version",
            tipoitemformularioid: 1,
            etiqueta: "Versión",
            requerido: true
          },
          {
            nombre: "archivo",
            tipoitemformularioid: 10,
            etiqueta: "Archivo",
            requerido: true
          },
          {
            nombre: "notasCambios",
            tipoitemformularioid: 11,
            etiqueta: "Notas de cambios"
          }
        ]
      }
    ],
    botones: [
      { etiqueta: "Crear versión", callback: crearVersion, validar: true }
    ]
  };

  return (
    <>
      <Toast ref={toast} position="top-center" />
      <ConfirmDialog />
      <Menu model={menuItems} popup ref={menuRef} />

      {!documento && (
        <div
          css={{
            marginTop: "2em",
            display: "flex",
            flexDirection: "column",
            gap: "1em"
          }}
        >
          <div
            css={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            }}
          >
            <div css={{ display: "flex", alignItems: "center", gap: "1em" }}>
              <Skeleton width="2rem" height="2rem" shape="circle" />
              <Skeleton width="15rem" height="2rem" />
            </div>
          </div>
          <Card>
            <Skeleton
              width="6rem"
              height="1.5rem"
              css={{ marginBottom: "1.5em" }}
            />
            <div className="grid">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="col-12 md:col-3">
                  <Skeleton
                    width="50%"
                    height="0.8rem"
                    css={{ marginBottom: "0.5rem" }}
                  />
                  <Skeleton width="80%" height="1.2rem" />
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {documento && (
        <>
          <Titulo
            urlVolver="/documento"
            etiqueta={documento.externo ? "Externo" : undefined}
            botones={
              <>
                {!documento.externo &&
                  documento.documentoVersions.some((v) => v.liberado) && (
                    <Boton transparente callback={() => setVisorVigente(true)}>
                      Vista previa
                    </Boton>
                  )}
                {!documento.externo &&
                  documento.documentoVersions.some((v) => v.liberado) && (
                    <Boton
                      transparente
                      callback={descargarVersionVigente}
                      loader={descargandoVigente}
                    >
                      Descargar versión vigente
                    </Boton>
                  )}
                <Boton
                  rojo
                  transparente
                  callback={eliminarDocumento}
                  loader={eliminandoDocumento}
                >
                  Eliminar
                </Boton>
              </>
            }
          >
            {documento.codigo} - {documento.nombre}
          </Titulo>

          {/* Vista previa de la versión vigente (la última liberada), con el
              mismo archivo que descarga "Descargar versión vigente": el PDF
              generado si existe, si no el original. */}
          {(() => {
            const vigente = documento.documentoVersions.find((v) => v.liberado);
            const archivo = vigente?.archivoPDF || vigente?.archivo;
            return (
              <VisorArchivo
                visible={visorVigente}
                onHide={() => setVisorVigente(false)}
                nombre={archivo?.nombredescarga}
                hash={archivo?.hash}
                titulo={
                  vigente
                    ? `${documento.codigo} - ${documento.nombre} - v.${vigente.version}`
                    : ""
                }
              />
            );
          })()}

          <Card>
            <Titulo
              botones={
                <Boton icono negro callback={() => setSideBarEditar(true)}>
                  <i className="pi pi-pencil" />
                </Boton>
              }
            >
              Detalles
            </Titulo>
            <div className="grid">
              <CampoInformacion nombreCampo="Código">
                {documento.codigo}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Nombre">
                {documento.nombre}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Carpeta">
                {documento.documentoCarpeta?.nombre || "-"}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Vigente">
                {documento.vigente ? "Sí" : "No"}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Disponible para colaboradores">
                {documento.disponibleAuditor ? "Sí" : "No"}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Disponible público">
                {documento.disponiblePublico ? "Sí" : "No"}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Disponible para el asistente de clientes">
                {documento.disponibleAsistente ? "Sí" : "No"}
              </CampoInformacion>
              {!documento.externo && (
                <CampoInformacion nombreCampo="Convertir a PDF">
                  {documento.convertirPDF ? "Sí" : "No"}
                </CampoInformacion>
              )}
              {documento.externo && (
                <CampoInformacion nombreCampo="Link externo">
                  {documento.linkExterno ? (
                    <a href={documento.linkExterno} target="_blank" rel="noreferrer">
                      {documento.linkExterno}
                    </a>
                  ) : (
                    "-"
                  )}
                </CampoInformacion>
              )}
            </div>
            <Divider />
            <div className="grid">
              <CampoInformacion nombreCampo="Lista de distribución" anchoCompleto>
                {documento.listaDistribucion || "-"}
              </CampoInformacion>
            </div>
            <Divider />
            <div className="grid">
              <CampoInformacion nombreCampo="Resumen" anchoCompleto>
                {documento.resumen}
              </CampoInformacion>
            </div>
          </Card>

          <Card css={{ marginTop: "1em" }}>
            <Titulo
              botones={
                <Boton
                  texto
                  callback={() => {
                    setEditandoInstrucciones(
                      !documento.instruccionesAlAsistente
                    );
                    setSideBarInstrucciones(true);
                  }}
                >
                  {documento.instruccionesAlAsistente
                    ? "Ver instrucciones"
                    : "Cargar instrucciones"}
                </Boton>
              }
            >
              Instrucciones para el asistente de IA
            </Titulo>
            <p css={{ color: "var(--grisTextos)" }}>
              {documento.instruccionesAlAsistente
                ? "Instrucciones cargadas."
                : "Sin instrucciones cargadas."}
            </p>
          </Card>

          {!documento.externo && <Card css={{ marginTop: "1em" }}>
            <Titulo
              botones={
                <Boton icono negro callback={() => setSideBar(true)}>
                  <i className="pi pi-plus" />
                </Boton>
              }
            >
              Versiones
            </Titulo>
            <TablaFiltrable conFiltros={false}
              key={`versiones-${descargandoId}`}
              value={documento.documentoVersions}
              emptyMessage="No hay versiones"
              stripedRows
              paginator
              rows={10}
              nombreArchivo="versiones_documento"
            >
              <Column field="version" header="Versión" />
              <Column field="notasCambios" header="Notas de Cambios" body={(rowData) => <span style={{ whiteSpace: "pre-wrap" }}>{rowData.notasCambios}</span>} />
              <Column
                header="Liberado"
                body={(rowData) => (
                  <Tag
                    value={rowData.liberado ? "Sí" : "No"}
                    severity={rowData.liberado ? "success" : "warning"}
                  />
                )}
                style={{ width: "8rem" }}
              />
              <Column
                header="Fecha Liberación"
                body={(rowData) =>
                  rowData.fechaLiberacion
                    ? formatFecha(rowData.fechaLiberacion)
                    : "-"
                }
                style={{ width: "12rem" }}
              />
              <Column
                header="Comparación"
                body={(rowData) => {
                  const estado = rowData.estadoComparacion?.status;
                  if (estado === "procesando") {
                    return (
                      <span css={{ display: "inline-flex", alignItems: "center", gap: "0.4em", color: "var(--text-color-secondary)" }}>
                        <i className="pi pi-spin pi-spinner" />
                        Comparando...
                      </span>
                    );
                  }
                  if (estado === "error") {
                    return <Tag value="Error" severity="danger" />;
                  }
                  if (estado === "success") {
                    return <Tag value="Listo" severity="success" />;
                  }
                  return "-";
                }}
                style={{ width: "10rem" }}
              />
              <Column
                header="Asistente IA"
                body={(rowData) => {
                  const estado = rowData.estadoExtraccion;
                  if (estado === "PROCESANDO") {
                    return (
                      <span css={{ display: "inline-flex", alignItems: "center", gap: "0.4em", color: "var(--text-color-secondary)" }}>
                        <i className="pi pi-spin pi-spinner" />
                        Extrayendo...
                      </span>
                    );
                  }
                  if (estado === "ERROR") {
                    return (
                      <span title={rowData.detalleErrorExtraccion || ""}>
                        <Tag value="Error" severity="danger" />
                      </span>
                    );
                  }
                  if (estado === "LISTO") {
                    return <Tag value="Listo" severity="success" />;
                  }
                  return "-";
                }}
                style={{ width: "10rem" }}
              />
              <Column
                body={(rowData) => {
                  const descargando = descargandoId === rowData.id;
                  return (
                    <div css={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0.5em" }}>
                      {descargando && <i className="pi pi-spin pi-spinner" />}
                      <Boton
                        icono
                        negro
                        callback={(e) => {
                          setVersionSeleccionada(rowData);
                          menuRef.current.toggle(e);
                        }}
                      >
                        <i className="pi pi-ellipsis-v" />
                      </Boton>
                    </div>
                  );
                }}
                style={{ width: "5rem" }}
              />
            </TablaFiltrable>
          </Card>}

          <div className="grid" css={{ marginTop: "1em" }}>
            <div className="col-12 lg:col-6">
              <TarjetaAsignarNorma
                normas={documento.documentoXNormas}
                documentoId={documento.id}
              />
            </div>
            <div className="col-12 lg:col-6">
              <TarjetaAsignarEsquema
                esquemas={documento.documentoXEsquemas}
                documentoId={documento.id}
              />
            </div>
          </div>

          <Sidebar
            visible={sideBar}
            position="right"
            onHide={() => setSideBar(false)}
            className="w-full lg:w-30rem md:w-30rem"
          >
            <Titulo>Nueva versión</Titulo>
            <EtiquetasDisponibles />
            <Formulario config={nuevaVersionFormConfig} loader={formLoader} />
          </Sidebar>

          {/* Sidebar de las instrucciones para el asistente (vista / edición) */}
          <Sidebar
            visible={sideBarInstrucciones}
            position="right"
            onHide={() => {
              setSideBarInstrucciones(false);
              setEditandoInstrucciones(false);
            }}
            className="w-full lg:w-8"
          >
            <Titulo
              botones={
                !editandoInstrucciones ? (
                  <Boton
                    icono
                    negro
                    callback={() => setEditandoInstrucciones(true)}
                  >
                    <i className="pi pi-pencil" />
                  </Boton>
                ) : null
              }
            >
              Instrucciones para el asistente de IA
            </Titulo>

            {editandoInstrucciones ? (
              <Formulario
                config={instruccionesFormConfig}
                loader={loaderInstrucciones}
              />
            ) : (
              <div css={{ whiteSpace: "pre-wrap", lineHeight: 1.5 }}>
                {documento.instruccionesAlAsistente}
              </div>
            )}
          </Sidebar>

          <Sidebar
            visible={sideBarEditar}
            position="right"
            onHide={() => setSideBarEditar(false)}
            className="w-full lg:w-30rem md:w-30rem"
          >
            <Titulo>Editar documento</Titulo>
            {editarFormConfig && (
              <Formulario
                key={documento.id + "-" + documento.codigo}
                config={editarFormConfig}
                loader={loaderEditar}
              />
            )}
          </Sidebar>

          <Sidebar
            visible={sideBarEditarVersion}
            position="right"
            onHide={() => setSideBarEditarVersion(false)}
            className="w-full lg:w-30rem md:w-30rem"
          >
            {versionSeleccionada && (
              <>
                <Titulo>Editar versión {versionSeleccionada.version}</Titulo>
                <EtiquetasDisponibles />
                <Formulario
                  key={versionSeleccionada.id + "-" + versionSeleccionada.version}
                  config={{
                    secciones: [
                      {
                        campos: [
                          {
                            nombre: "version",
                            tipoitemformularioid: 1,
                            etiqueta: "Versión",
                            requerido: true,
                            respuesta: versionSeleccionada.version
                          },
                          {
                            nombre: "archivo",
                            tipoitemformularioid: 10,
                            etiqueta: "Reemplazar archivo"
                          },
                          {
                            nombre: "notasCambios",
                            tipoitemformularioid: 11,
                            etiqueta: "Notas de cambios",
                            respuesta: versionSeleccionada.notasCambios || ""
                          }
                        ]
                      }
                    ],
                    botones: [
                      {
                        etiqueta: "Guardar cambios",
                        callback: editarVersionSubmit,
                        validar: true
                      }
                    ]
                  }}
                  loader={loaderEditarVersion}
                />
              </>
            )}
          </Sidebar>

          <Sidebar
            visible={sideBarDetalleVersion}
            position="right"
            onHide={() => setSideBarDetalleVersion(false)}
            className="w-full lg:w-30rem md:w-30rem"
          >
            {versionSeleccionada && (
              <>
                <Titulo>Detalles de versión {versionSeleccionada.version}</Titulo>
                <div className="grid">
                  <CampoInformacion nombreCampo="Versión">
                    {versionSeleccionada.version}
                  </CampoInformacion>
                  <CampoInformacion nombreCampo="Liberado">
                    <Tag
                      value={versionSeleccionada.liberado ? "Sí" : "No"}
                      severity={versionSeleccionada.liberado ? "success" : "warning"}
                    />
                  </CampoInformacion>
                  <CampoInformacion nombreCampo="Fecha de liberación">
                    {versionSeleccionada.fechaLiberacion
                      ? formatFecha(versionSeleccionada.fechaLiberacion)
                      : "-"}
                  </CampoInformacion>
                </div>
                <Divider />
                <div className="grid">
                  <CampoInformacion nombreCampo="Notas de cambios" anchoCompleto>
                    <span style={{ whiteSpace: "pre-wrap" }}>
                      {versionSeleccionada.notasCambios || "-"}
                    </span>
                  </CampoInformacion>
                </div>
                <Divider />
                <Titulo>Control de cambios</Titulo>
                {versionSeleccionada.estadoComparacion?.status === "procesando" ? (
                  <div css={{ display: "flex", alignItems: "center", gap: "0.5em", color: "var(--text-color-secondary)" }}>
                    <i className="pi pi-spin pi-spinner" />
                    Generando comparación con IA, puede demorar unos minutos...
                  </div>
                ) : versionSeleccionada.estadoComparacion?.status === "error" ? (
                  <div css={{ color: "var(--rojo, #c0392b)" }}>
                    No se pudo generar el control de cambios automático
                    {versionSeleccionada.estadoComparacion?.mensaje
                      ? `: ${versionSeleccionada.estadoComparacion.mensaje}`
                      : "."}
                  </div>
                ) : Array.isArray(versionSeleccionada.controlCambios) &&
                versionSeleccionada.controlCambios.length > 0 ? (
                  <div css={{ display: "flex", flexDirection: "column" }}>
                    {versionSeleccionada.controlCambios.map((cambio, idx) => (
                      <div
                        key={idx}
                        css={{
                          padding: "0.75em 0",
                          borderBottom:
                            idx < versionSeleccionada.controlCambios.length - 1
                              ? "1px solid var(--surface-border)"
                              : "none"
                        }}
                      >
                        <div
                          css={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.5em",
                            marginBottom: "0.25em"
                          }}
                        >
                          <Tag
                            value={cambio.tipo}
                            severity={
                              cambio.tipo === "agregado"
                                ? "success"
                                : cambio.tipo === "eliminado"
                                ? "danger"
                                : "warning"
                            }
                          />
                          <span
                            css={{
                              fontWeight: 600,
                              color: "var(--text-color-secondary)",
                              fontSize: "0.9em"
                            }}
                          >
                            {cambio.ubicacion || "Sin ubicación"}
                          </span>
                        </div>
                        <div css={{ lineHeight: 1.5 }}>
                          {cambio.descripcion}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div css={{ color: "var(--text-color-secondary)" }}>
                    No hay control de cambios registrado para esta versión.
                  </div>
                )}
              </>
            )}
          </Sidebar>
        </>
      )}
    </>
  );
}
